import type { ReactNode } from "react"
import { Stack, Typography } from "@mui/material"
import { faBarcodeRead, faBuilding, faCalendar } from "@fortawesome/pro-light-svg-icons"
import type { IconDefinition } from "@fortawesome/pro-light-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { makeStyles } from "tss-react/mui"

import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { useDateTime } from "../../../../../localization/useDateTime"

import { formatCardNumber } from "../idCardsListHelpers"
import { IdCardHolderKind, IdCardStatus } from "../idCardsListTypes"
import type { IdCardRow } from "../idCardsListTypes"

const useStyles = makeStyles()((theme) => ({
    number: {
        fontVariantNumeric: "tabular-nums",
        letterSpacing: ".02rem",
        color: theme.palette.tailwind.slate[800],
    },
    meta: {
        color: theme.palette.tailwind.slate[500],
    },
    noHolder: {
        color: theme.palette.tailwind.slate[400],
    },
    scansUsed: {
        color: theme.palette.tailwind.green[700],
        fontWeight: 600,
    },
    scansLimitReached: {
        color: theme.palette.tailwind.red[700],
        fontWeight: 600,
    },
}))

type MetaItemProps = Readonly<{
    icon: IconDefinition
    className?: string
    children: ReactNode
}>

function MetaItem({ icon, className, children }: MetaItemProps) {
    return (
        <Stack direction="row" alignItems="center" gap=".25rem" className={className}>
            <FontAwesomeIcon icon={icon} />
            {children}
        </Stack>
    )
}

function HolderLabel({ card }: Readonly<{ card: IdCardRow }>) {
    const { classes } = useStyles()

    if (!card.holder) {
        return (
            <LocalizedStrict id="merchant-id-cards-list-holder-none">
                <span className={classes.noHolder}>No cardholder</span>
            </LocalizedStrict>
        )
    }

    if (card.holder.kind === IdCardHolderKind.COMPANY) {
        return (
            <LocalizedStrict id="merchant-id-cards-list-holder-guest">
                <span>Guest card — no named holder</span>
            </LocalizedStrict>
        )
    }

    return <span>{card.holder.name}</span>
}

function ScansToday({ card }: Readonly<{ card: IdCardRow }>) {
    const { classes } = useStyles()
    const { scansToday, scansPerDay } = card

    if (!scansToday) {
        return (
            <MetaItem icon={faBarcodeRead}>
                <LocalizedStrict id="merchant-id-cards-list-scans-none">
                    <span>Not scanned today</span>
                </LocalizedStrict>
            </MetaItem>
        )
    }

    const isLimitReached = !!scansPerDay && scansToday >= scansPerDay

    return (
        <MetaItem icon={faBarcodeRead} className={isLimitReached ? classes.scansLimitReached : classes.scansUsed}>
            {scansPerDay ? (
                <LocalizedStrict
                    id="merchant-id-cards-list-scans-today-limited"
                    vars={{ count: scansToday, limit: scansPerDay }}
                >
                    <span>
                        {scansToday} of {scansPerDay} scans today
                    </span>
                </LocalizedStrict>
            ) : (
                <LocalizedStrict id="merchant-id-cards-list-scans-today" vars={{ count: scansToday }}>
                    <span>{scansToday} scans today</span>
                </LocalizedStrict>
            )}
        </MetaItem>
    )
}

function ValidPeriod({ validFrom, validTo }: Readonly<{ validFrom: string; validTo: string | undefined }>) {
    const dateTimeFactory = useDateTime()

    return (
        <MetaItem icon={faCalendar}>
            <span>{dateTimeFactory.formatMonthDay(dateTimeFactory.fromApi(validFrom))} →</span>
            {validTo ? (
                <span>{dateTimeFactory.formatMonthDay(dateTimeFactory.fromApi(validTo))}</span>
            ) : (
                <LocalizedStrict id="merchant-id-cards-list-valid-no-end">
                    <span>no end</span>
                </LocalizedStrict>
            )}
        </MetaItem>
    )
}

export function IdCardNumberCell({ card }: Readonly<{ card: IdCardRow }>) {
    const { classes } = useStyles()

    return (
        <Stack gap=".25rem">
            <Typography variant="body1" fontWeight={700} className={classes.number}>
                {formatCardNumber(card.cardNumber)}
            </Typography>
            <Typography variant="caption" component="div" className={classes.meta}>
                <Stack direction="row" flexWrap="wrap" alignItems="center" columnGap=".75rem" rowGap=".25rem">
                    <HolderLabel card={card} />
                    {card.status === IdCardStatus.ASSIGNED && <ScansToday card={card} />}
                    {card.validFrom && <ValidPeriod validFrom={card.validFrom} validTo={card.validTo} />}
                    {card.holder?.kind === IdCardHolderKind.COMPANY && card.company && (
                        <MetaItem icon={faBuilding}>
                            <span>{card.company.name}</span>
                        </MetaItem>
                    )}
                </Stack>
            </Typography>
        </Stack>
    )
}
